import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { AlertCircle, Plus, Download, Trash2, Eye } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';

export const Incidents = () => {
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchIncidents();
  }, []);

  const fetchIncidents = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;

      const response = await fetch(`${backendUrl}/api/incidents`, {
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to fetch incidents');

      const data = await response.json();
      setIncidents(data);
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Failed to load incidents');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (incidentId) => {
    if (!confirm('Delete this incident recording?')) return;

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;

      const response = await fetch(`${backendUrl}/api/incidents/${incidentId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Delete failed');

      toast.success('Incident deleted');
      setIncidents(incidents.filter(i => i.incident_id !== incidentId));
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete incident');
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <Skeleton key={i} className="h-32" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-semibold tracking-tight flex items-center gap-3">
            <AlertCircle className="h-8 w-8" />
            Incident Recordings
          </h1>
          <p className="text-muted-foreground mt-1">Audio evidence and emergency records</p>
        </div>
        <Link to="/app/incidents/record">
          <Button className="gap-2 bg-red-500 hover:bg-red-600">
            <Plus className="h-5 w-5" />
            Record Incident
          </Button>
        </Link>
      </div>

      {/* Incidents List */}
      {incidents.length === 0 ? (
        <Card className="p-12 text-center">
          <AlertCircle className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">No Incidents Recorded</h3>
          <p className="text-muted-foreground mb-4">
            Record audio evidence in emergencies or accidents
          </p>
          <Link to="/app/incidents/record">
            <Button>Record First Incident</Button>
          </Link>
        </Card>
      ) : (
        <div className="grid gap-4">
          {incidents.map((incident, idx) => (
            <motion.div
              key={incident.incident_id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 }}
            >
              <Card className="card-hover">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold">{incident.title}</h3>
                        <Badge variant={
                          incident.severity === 'critical' ? 'destructive' :
                          incident.severity === 'severe' ? 'default' :
                          'secondary'
                        }>
                          {incident.severity}
                        </Badge>
                        <Badge variant="outline">
                          {incident.incident_type.replace('_', ' ')}
                        </Badge>
                      </div>
                      
                      <p className="text-sm text-muted-foreground mb-3">
                        {new Date(incident.timestamp).toLocaleString('en-GB')}
                      </p>

                      <div className="flex items-center gap-4 text-sm">
                        {incident.audio_url && (
                          <span className="flex items-center gap-1 text-green-600">
                            ✓ Audio
                          </span>
                        )}
                        {incident.transcription && (
                          <span className="flex items-center gap-1 text-blue-600">
                            ✓ Transcribed
                          </span>
                        )}
                        {incident.photos && incident.photos.length > 0 && (
                          <span className="flex items-center gap-1 text-purple-600">
                            ✓ {incident.photos.length} Photos
                          </span>
                        )}
                        {incident.location && (
                          <span className="flex items-center gap-1 text-orange-600">
                            ✓ GPS
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Link to={`/app/incidents/${incident.incident_id}`}>
                        <Button variant="outline" size="sm" className="gap-2">
                          <Eye className="h-4 w-4" />
                          View
                        </Button>
                      </Link>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(incident.incident_id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Incidents;
